import { FC, useCallback, useState } from 'react'

import {
  BulkActionsDefinition,
  FiltersDefinition,
  OnBulkActionCallback,
} from '@factorialco/f0-react/dist/experimental'
import { ExpensablesDataCollectionQuery } from 'generated/resources/graphql'

import i18n from 'modules/core/lib/i18n'

import AddToGroupModal from 'modules/expenses/components/expenses/AddToGroupModal'
import RemoveFromGroupModal from 'modules/expenses/components/expenses/RemoveFromGroupModal'
import { useExpensableActions } from 'modules/expenses/hooks/useExpensableActions'

type Expensable =
  ExpensablesDataCollectionQuery['expenses']['expensablesConnection']['nodes'][number]

interface UseMyExpensesBulkActionsProps {
  expensableGroupsEnabled: boolean
  employeeId: number
}

type ModalState =
  | { type: 'closed' }
  | { type: 'add-to-group'; expensables: Expensable[] }
  | { type: 'remove-from-group'; expensables: Expensable[] }

export const useMyExpensesBulkActions = ({
  expensableGroupsEnabled,
  employeeId,
}: UseMyExpensesBulkActionsProps) => {
  const [modalState, setModalState] = useState<ModalState>({ type: 'closed' })
  const { onAddToGroup, onRemoveFromGroup } = useExpensableActions()

  const closeModal = () => setModalState({ type: 'closed' })

  const bulkActions = useCallback<BulkActionsDefinition<Expensable, FiltersDefinition<string>>>(
    ({ itemsStatus }) => {
      if (!expensableGroupsEnabled) {
        return { primary: [] }
      }

      const selectedExpensables = itemsStatus
        .filter((item) => item.checked)
        .map((item) => item.item)

      if (selectedExpensables.length === 0) {
        return { primary: [] }
      }

      const canAddToGroup = selectedExpensables.some(
        (exp) => exp.status === 'draft' && !exp.groupId && exp.owner?.id === employeeId
      )

      const canRemoveFromGroup = selectedExpensables.some(
        (exp) => exp.status === 'draft' && exp.groupId
      )

      if (!canAddToGroup && !canRemoveFromGroup) {
        return { primary: [] }
      }

      const actions = []

      if (canAddToGroup) {
        actions.push({
          id: 'add-to-group',
          label: i18n.t('expenses_v2.buttons.add_to_group_v2'),
        })
      }

      if (canRemoveFromGroup) {
        actions.push({
          id: 'remove-from-group',
          label: i18n.t('expenses_v2.buttons.remove_from_group_v2'),
        })
      }

      return { primary: actions }
    },
    [expensableGroupsEnabled, employeeId]
  )

  const handleBulkAction = useCallback<OnBulkActionCallback<Expensable, FiltersDefinition<string>>>(
    async (actionId, { itemsStatus }) => {
      const selectedExpensables = itemsStatus
        .filter((item) => item.checked)
        .map((item) => item.item)

      if (actionId === 'add-to-group') {
        const eligibleExpensables = selectedExpensables.filter(
          (exp) => exp.status === 'draft' && !exp.groupId && exp.owner?.id === employeeId
        )

        if (eligibleExpensables.length > 0) {
          setModalState({ type: 'add-to-group', expensables: eligibleExpensables })
        }
      } else if (actionId === 'remove-from-group') {
        const eligibleExpensables = selectedExpensables.filter(
          (exp) => exp.status === 'draft' && exp.groupId
        )

        if (eligibleExpensables.length > 0) {
          setModalState({ type: 'remove-from-group', expensables: eligibleExpensables })
        }
      }
    },
    [employeeId]
  )

  const handleAddToGroup = async (groupId: number) => {
    if (modalState.type !== 'add-to-group') return

    const ids = modalState.expensables.map((exp) => exp.id)
    try {
      await onAddToGroup(ids, groupId)
    } finally {
      closeModal()
    }
  }

  const handleRemoveFromGroup = async () => {
    if (modalState.type !== 'remove-from-group') return

    const ids = modalState.expensables.map((exp) => exp.id)
    try {
      await onRemoveFromGroup(ids)
    } finally {
      closeModal()
    }
  }

  const Modals: FC = () => {
    if (modalState.type === 'closed') return null

    switch (modalState.type) {
      case 'add-to-group':
        return <AddToGroupModal onSubmit={handleAddToGroup} onClose={closeModal} />

      case 'remove-from-group':
        return <RemoveFromGroupModal onSubmit={handleRemoveFromGroup} onClose={closeModal} />
    }
  }

  const handlers = {
    openAddToGroupModal: (expensables: Expensable[]) =>
      setModalState({ type: 'add-to-group', expensables }),
    openRemoveFromGroupModal: (expensables: Expensable[]) =>
      setModalState({ type: 'remove-from-group', expensables }),
  }

  return {
    bulkActions,
    handleBulkAction,
    handlers,
    Modals,
  }
}
